import { useEffect, useState } from 'react'

/**
 * True when the device both exposes the Web Share API AND is primarily
 * touch-driven. Both signals are required on purpose: some desktop browsers
 * (Safari on macOS, some Chromium builds) implement `navigator.share` too,
 * but the desktop share UI here is the three per-network icon buttons, not
 * the OS share sheet — so a mouse-driven browser must fall through to that
 * branch even when `navigator.share` exists. `(pointer: coarse)` is a
 * feature-detection media query (the primary pointer can't accurately hover
 * or click small targets), not user-agent sniffing, so it degrades safely on
 * any engine that doesn't recognise it.
 */
export function canUseNativeShare(): boolean {
  const hasShareApi = typeof navigator.share === 'function'
  const isCoarsePointer =
    typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches

  return hasShareApi && isCoarsePointer
}

/**
 * Reactive wrapper around {@link canUseNativeShare} for `ShareButtons`. Reads
 * the feature/media-query pair once on mount and again whenever the
 * `(pointer: coarse)` query flips (e.g. a tablet docked with a mouse), so the
 * rendered share UI never gets stuck on the value read at first paint.
 */
export function useNativeShareSupport(): boolean {
  const [supported, setSupported] = useState(canUseNativeShare)

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined

    const pointerQuery = window.matchMedia('(pointer: coarse)')
    const update = () => setSupported(canUseNativeShare())

    update()
    pointerQuery.addEventListener('change', update)
    return () => pointerQuery.removeEventListener('change', update)
  }, [])

  return supported
}
